import { IEvidence, ProgramData, ProgramSkill } from "@interfaces/Interfaces";
import { addDays, addMonths, isBefore, startOfDay } from "date-fns";
import * as EvidenceUtils from "@utils/evidenceUtils";
import * as ProgramUtils from "@utils/programUtils";
import React from "react";

const COMPLIANCE_MONTHS = 12;
const WARNING_DAYS = 30;

type SkillsRAGDisplayRenfrewProps = {
  skills: ProgramSkill[];
  evidence: IEvidence[];
  program: ProgramData;
  section: string;
};

type ComplianceStatus = "compliant" | "warning" | "attention";

const evidenceIncludesSkill = (evidence: IEvidence, skill: ProgramSkill, program: ProgramData): boolean => {
  if (evidence.draft || evidence.evidenceJSON?.OnHoldReset === 1) {
    return false;
  }

  return ProgramUtils.evidenceMatchesSkill(evidence, skill, program);
};

const latestEvidenceDate = (skill: ProgramSkill, evidence: IEvidence[], program: ProgramData): Date | undefined => {
  const timestamps = evidence
    .filter((item) => evidenceIncludesSkill(item, skill, program))
    .map((item) => EvidenceUtils.getRenfrewEvidenceDate(item)?.getTime())
    .filter((time): time is number => time !== undefined && !isNaN(time));

  return timestamps.length > 0 ? startOfDay(new Date(Math.max(...timestamps))) : undefined;
};

const complianceStatus = (latest: Date | undefined, today: Date): ComplianceStatus => {
  if (!latest) {
    return "attention";
  }

  const due = addMonths(latest, COMPLIANCE_MONTHS);

  if (isBefore(due, today)) {
    return "attention";
  }

  const warningLimit = addDays(today, WARNING_DAYS);

  if (!isBefore(warningLimit, due)) {
    return "warning";
  }

  return "compliant";
};

const SkillsRAGDisplayRenfrew: React.FC<SkillsRAGDisplayRenfrewProps> = (props) => {
  const skillsCount = props.skills.length;
  const today = startOfDay(new Date());
  const statuses = props.skills.map((skill) =>
    complianceStatus(latestEvidenceDate(skill, props.evidence, props.program), today)
  );

  const greenSkillsCount = statuses.filter((status) => status === "compliant").length;
  const amberSkillsCount = statuses.filter((status) => status === "warning").length;
  const redSkillsCount = statuses.filter((status) => status === "attention").length;

  const greenSkillsPercentage = Math.floor((greenSkillsCount / skillsCount) * 100);
  const amberSkillsPercentage = Math.floor((amberSkillsCount / skillsCount) * 100);
  const redSkillsPercentage = Math.floor((redSkillsCount / skillsCount) * 100);

  return (
    <div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-0">
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-green-light-text">
              {greenSkillsCount}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} ${props.section === "CPGs" ? "CPGs" : "skills"}`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${greenSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Compliant".toUpperCase()}
          </div>
        </div>
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-orange-50">
              {amberSkillsCount}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} ${props.section === "CPGs" ? "CPGs" : "skills"}`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${amberSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Warning".toUpperCase()}
          </div>
        </div>
        <div className="flex grow flex-col">
          <div className="flex flex-row items-end">
            <div className="text-acc-34px font-acc-700 leading-none tracking-[-0.5] text-grey-60">{redSkillsCount}</div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-60">
              &nbsp;{`/ ${skillsCount} ${props.section === "CPGs" ? "CPGs" : "skills"}`}
            </div>
            <div className="mb-1 text-acc-15px font-acc-700 leading-none tracking-default text-grey-90">
              &nbsp;{`(${redSkillsPercentage}%)`}
            </div>
          </div>
          <div className="mt-1.5 text-acc-11px font-acc-600 leading-none tracking-default text-grey-60">
            {"Attention Required".toUpperCase()}
          </div>
        </div>
      </div>
      <div className="mt-2 flex flex-col">
        <div className="flex h-14 flex-row items-center justify-between">
          <div>{"Compliant"}</div>
          <div className="flex size-7 items-center justify-center rounded-full bg-green-light-text">
            <div className="text-12px font-semibold text-white">{`${greenSkillsCount}`}</div>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-30" />
      </div>
      <div className="flex flex-col">
        <div className="flex h-14 flex-row items-center justify-between">
          <div>{"Warning"}</div>
          <div className="flex size-7 items-center justify-center rounded-full bg-orange-400">
            <div className="text-12px font-semibold text-white">{`${amberSkillsCount}`}</div>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-30" />
      </div>
      <div className="flex flex-col">
        <div className="flex h-14 flex-row items-center justify-between">
          <div>{"Attention Required"}</div>
          <div className="flex size-7 items-center justify-center rounded-full bg-cta-red">
            <div className="text-12px font-semibold text-white">{`${redSkillsCount}`}</div>
          </div>
        </div>
        <div className="h-[1px] w-full bg-grey-30" />
      </div>
    </div>
  );
};

export default SkillsRAGDisplayRenfrew;
